import * as React from 'react';
import { useState } from 'react';
import { TextInputField } from '../../../components/Forms/fields';
import { ComboField } from '../../../components/Forms/fields/ComboField';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { ClientPropertySettings, FixedClientPropertySettings, GeneralSettingsData } from '../../../models';
import { SettingsItemContentProps } from '../SettingsItem';
import StyledTable from '../../../components/StyledTable';
import AppButton from '../../../components/AppButton';

interface ClientProfileSettingsProps extends SettingsItemContentProps {
    values: GeneralSettingsData;
    onChange: (settings: GeneralSettingsData) => void;
}

const defaultClientPropertySettings: ClientPropertySettings = { type: 'text' };
const clientPropertyTypeOptions = ['text', 'multiline', 'number'] as const;
type ClientPropertyType = typeof clientPropertyTypeOptions[number];
const clientPropertyWidthOptions = ['full', 'medium', 'small'] as const;
type ClientPropertyWidth = typeof clientPropertyWidthOptions[number];

const FIXED_FIELDS: Array<{ id: string; labelKey: TK }> = [
    { id: 'IdentificationNumber', labelKey: TK.identificationNumber },
    { id: 'TaxNumber',            labelKey: TK.taxNumber },
    { id: 'HealthNumber',         labelKey: TK.healthNumber },
    { id: 'SubsystemName',        labelKey: TK.subsystemName },
    { id: 'SubsystemNumber',      labelKey: TK.subsystemNumber },
    { id: 'Sex',                  labelKey: TK.sex },
    { id: 'Birthday',             labelKey: TK.birthdate },
    { id: 'Height',               labelKey: TK.height },
    { id: 'Job',                  labelKey: TK.job },
    { id: 'Origin',               labelKey: TK.origin },
    { id: 'Address',              labelKey: TK.address },
    { id: 'ZipCode',              labelKey: TK.zipCode },
    { id: 'City',                 labelKey: TK.city },
    { id: 'PhoneNumbers',         labelKey: TK.phoneNumbers },
    { id: 'Email',                labelKey: TK.email },
    { id: 'WarningNote',          labelKey: TK.warning },
    { id: 'Notes',                labelKey: TK.notes },
];

const NameCell: React.FC<{ name: string; onRename: (newName: string) => void }> = ({ name, onRename }) => {
    const [value, setValue] = useState(name);
    return (
        <TextInputField
            value={value}
            onChange={setValue}
            onBlur={() => { if (value.trim() && value !== name) onRename(value.trim()); }}
        />
    );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h5 style={{ fontWeight: 600, color: 'var(--text-muted)', marginBottom: 'var(--spacer-M)' }}>{children}</h5>
);

const ClientProfileSettings: React.FC<ClientProfileSettingsProps> = ({ values, onChange }) => {
    const t = useTranslations();

    const clientProperties = values.clientProperties ?? {};
    const fixedClientProperties = values.fixedClientProperties ?? {};

    const onPropertiesChange = (props: Record<string, ClientPropertySettings>) =>
        onChange({ ...values, clientProperties: props });

    const onFixedPropertiesChange = (props: Record<string, FixedClientPropertySettings>) =>
        onChange({ ...values, fixedClientProperties: props });

    const typeLabel = (type: string): string => {
        if (type === 'multiline') return t(TK.multilineText);
        if (type === 'number') return t(TK.number);
        return t(TK.text);
    };

    const typeFromLabel = (label: string): ClientPropertyType => {
        if (label === t(TK.multilineText)) return 'multiline';
        if (label === t(TK.number)) return 'number';
        return 'text';
    };

    const widthLabel = (width: string | undefined): string => {
        if (width === 'medium') return t(TK.fieldWidthMedium);
        if (width === 'small') return t(TK.fieldWidthSmall);
        return t(TK.fullWidth);
    };

    const widthFromLabel = (label: string): ClientPropertyWidth => {
        if (label === t(TK.fieldWidthMedium)) return 'medium';
        if (label === t(TK.fieldWidthSmall)) return 'small';
        return 'full';
    };

    const handleRename = (oldName: string, newName: string) => {
        if (!newName || newName === oldName || newName in clientProperties) return;
        const entries = Object.entries(clientProperties);
        onPropertiesChange(Object.fromEntries(entries.map(([k, v]) => [k === oldName ? newName : k, v])));
    };

    const handleRemove = (name: string) => {
        const { [name]: _, ...rest } = clientProperties;
        onPropertiesChange(rest);
    };

    const handleAdd = () => {
        let base = t(TK.name);
        let candidate = base;
        let i = 1;
        while (candidate in clientProperties) candidate = `${base} ${i++}`;
        onPropertiesChange({ ...clientProperties, [candidate]: { ...defaultClientPropertySettings } });
    };

    const getFixed = (id: string): FixedClientPropertySettings => fixedClientProperties[id] ?? {};

    const setFixed = (id: string, patch: Partial<FixedClientPropertySettings>) =>
        onFixedPropertiesChange({ ...fixedClientProperties, [id]: { ...getFixed(id), ...patch } });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>

            <div style={{ marginTop: 'var(--spacer-M)' }}>
                <SectionTitle>{t(TK.additionalProperties)}</SectionTitle>
                <StyledTable scrollable size="sm">
                    <thead>
                        <tr>
                            <th>{t(TK.name)}</th>
                            <th>{t(TK.description)}</th>
                            <th>{t(TK.type)}</th>
                            <th>{t(TK.fieldWidth)}</th>
                            <th>{t(TK.unit)}</th>
                            {/* <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{t(TK.professionalCanView)}</th>
                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{t(TK.professionalCanEdit)}</th>
                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{t(TK.clientCanView)}</th>
                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{t(TK.clientCanEdit)}</th> */}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(clientProperties).map(([name, settings]) => (
                            <tr key={name}>
                                <td style={{ minWidth: '200px' }}>
                                    <NameCell name={name} onRename={newName => handleRename(name, newName)} />
                                </td>
                                <td style={{ minWidth: '250px' }}>
                                    <TextInputField
                                        value={settings.description ?? ''}
                                        onChange={description => onPropertiesChange({ ...clientProperties, [name]: { ...settings, description } })}
                                    />
                                </td>
                                <td style={{ minWidth: '130px' }}>
                                    <ComboField
                                        options={clientPropertyTypeOptions.map(typeLabel)}
                                        value={typeLabel(settings.type)}
                                        onChange={label => onPropertiesChange({ ...clientProperties, [name]: { ...settings, type: typeFromLabel(label ?? '') } })}
                                    />
                                </td>
                                <td style={{ minWidth: '110px' }}>
                                    <ComboField
                                        options={clientPropertyWidthOptions.map(widthLabel)}
                                        value={widthLabel(settings.width)}
                                        onChange={label => onPropertiesChange({ ...clientProperties, [name]: { ...settings, width: widthFromLabel(label ?? '') } })}
                                    />
                                </td>
                                <td style={{ minWidth: '90px' }}>
                                    <TextInputField
                                        value={settings.unit ?? ''}
                                        onChange={unit => onPropertiesChange({ ...clientProperties, [name]: { ...settings, unit } })}
                                    />
                                </td>
                                {/* <td style={{ textAlign: 'center' }}>
                                    <CheckInputField
                                        id={`prop-${name}-prof-view`}
                                        value={settings.professionalCanView ?? true}
                                        onChange={v => onPropertiesChange({ ...clientProperties, [name]: { ...settings, professionalCanView: v } })}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <CheckInputField
                                        id={`prop-${name}-prof-edit`}
                                        value={settings.professionalCanEdit ?? true}
                                        onChange={v => onPropertiesChange({ ...clientProperties, [name]: { ...settings, professionalCanEdit: v } })}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <CheckInputField
                                        id={`prop-${name}-client-view`}
                                        value={settings.clientCanView ?? false}
                                        onChange={v => onPropertiesChange({ ...clientProperties, [name]: { ...settings, clientCanView: v } })}
                                    />
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <CheckInputField
                                        id={`prop-${name}-client-edit`}
                                        value={settings.clientCanEdit ?? false}
                                        onChange={v => onPropertiesChange({ ...clientProperties, [name]: { ...settings, clientCanEdit: v } })}
                                    />
                                </td> */}
                                <td>
                                    <AppButton color="link" size="sm" style={{ color: 'var(--danger-color)' }} onClick={() => handleRemove(name)}>
                                        {t(TK.remove)}
                                    </AppButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </StyledTable>
                <div style={{ marginTop: 'var(--spacer-S)', display: 'flex', justifyContent: 'flex-end' }}>
                    <AppButton color="secondary" size="sm" onClick={handleAdd}>
                        + {t(TK.add)}
                    </AppButton>
                </div>
            </div>


            <div>
                <SectionTitle>{t(TK.builtInFields)}</SectionTitle>
                <StyledTable scrollable size="sm">
                    <thead>
                        <tr>
                            <th>{t(TK.name)}</th>
                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{t(TK.hide)}</th>
                            {/* <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{t(TK.clientCanView)}</th>
                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{t(TK.clientCanEdit)}</th> */}
                        </tr>
                    </thead>
                    <tbody>
                        {FIXED_FIELDS.map(({ id, labelKey }) => {
                            const s = getFixed(id);
                            return (
                                <tr key={id}>
                                    <td style={{ fontWeight: 500 }}>{t(labelKey)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <CheckInputField
                                            id={`fixed-${id}-hide`}
                                            value={s.hidden ?? false}
                                            onChange={v => setFixed(id, { hidden: v })}
                                        />
                                    </td>
                                    {/* <td style={{ textAlign: 'center' }}>
                                        <CheckInputField
                                            id={`fixed-${id}-client-view`}
                                            value={s.clientCanView ?? false}
                                            onChange={v => setFixed(id, { clientCanView: v })}
                                        />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <CheckInputField
                                            id={`fixed-${id}-client-edit`}
                                            value={s.clientCanEdit ?? false}
                                            onChange={v => setFixed(id, { clientCanEdit: v })}
                                        />
                                    </td> */}
                                </tr>
                            );
                        })}
                    </tbody>
                </StyledTable>
            </div>
        </div>
    );
};

export default ClientProfileSettings;
